import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { KeyRound, MapPin, ShieldCheck } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import PropertyBooking from "../components/commercial/PropertyBooking";
import { fmtCents, setPageTitle } from "../lib/format";

const RATE_LABEL = { hourly: "per hour", multi_hour: "flat", daily: "per day", flat: "flat", monthly: "per month", custom: "flat" };
const fmtDuration = (mins) => {
  if (mins % 43200 === 0) return `${mins / 43200}-month`;
  if (mins % 1440 === 0) return `${mins / 1440}-day`;
  if (mins % 60 === 0) return `${mins / 60}-hour`;
  return `${mins}-minute`;
};

// Public property landing (V2) — the destination for QR scans, deep links and
// homepage property-code entry. Server resolves the code; this page shows only
// the public-safe payload. Login preserves the destination via location.state.
export default function PropertyLanding() {
  const { code, categoryCode } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null); // { status, message }

  useEffect(() => {
    setPageTitle("Property");
    setData(null);
    setError(null);
    api.publicProperty(code, categoryCode)
      .then(setData)
      .catch((e) => setError({ status: e.status, message: e.message }));
  }, [code, categoryCode]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="property-landing">
      <Navbar />
      <main className="flex-1 max-w-4xl w-full mx-auto px-5 sm:px-8 pt-32 pb-16">
        {error ? (
          <div className="card-dark p-12 text-center" data-testid="property-error">
            <KeyRound className="w-10 h-10 mx-auto text-zinc-600" />
            <h1 className="type-h3 mt-5">{error.status === 409 ? "This property is currently unavailable." : "Property code not found."}</h1>
            <p className="text-sm text-zinc-500 mt-3 max-w-sm mx-auto">
              {error.status === 409
                ? "The operator has paused parking here. Check the posted sign or contact the property."
                : "Check the 4-digit code on the parking sign or your invite and try again."}
            </p>
            <Link to="/" className="btn-secondary inline-flex mt-7 text-sm" data-testid="property-error-home">Back to homepage</Link>
          </div>
        ) : !data ? (
          <div className="space-y-4"><div className="card-dark h-40 animate-pulse" /><div className="card-dark h-24 animate-pulse" /></div>
        ) : data.kind === "listing" ? (
          <div className="card-dark p-7 sm:p-9" data-testid="property-hero">
            <p className="section-label">MyRedPark listing</p>
            <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl" data-testid="property-name">{data.name}</h1>
                <p className="text-sm text-zinc-400 mt-2.5 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#FF3B45] shrink-0" /> {data.address}</p>
                {data.price_per_hour != null && (
                  <p className="text-sm text-zinc-300 mt-2" data-testid="property-rate">From {fmtCents(Math.round(data.price_per_hour * 100), data.currency)} / hour{data.space_type ? ` · ${data.space_type}` : ""}</p>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono font-extrabold text-3xl tracking-[0.2em] text-[#FF3B45]" data-testid="property-code">{data.property_code}</p>
                <p className="text-[9px] uppercase tracking-widest text-zinc-600 mt-0.5">Property code</p>
              </div>
            </div>
            <Link to={`/spot/${data.spot_id}?code=${data.property_code}`} className="btn-primary inline-flex mt-7 text-sm" data-testid="property-listing-continue">
              View availability &amp; book
            </Link>
            <p className="text-[11px] text-zinc-600 mt-3">Exact address is shared once your booking payment is authorized.</p>
          </div>
        ) : (
          <>
            <div className="card-dark p-7 sm:p-9" data-testid="property-hero">
              <p className="section-label">Commercial parking</p>
              <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl" data-testid="property-name">{data.name}</h1>
                  <p className="text-sm text-zinc-400 mt-2.5 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#FF3B45] shrink-0" /> {data.address}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono font-extrabold text-3xl tracking-[0.2em] text-[#FF3B45]" data-testid="property-code">{data.property_code}</p>
                  <p className="text-[9px] uppercase tracking-widest text-zinc-600 mt-0.5">Property code</p>
                </div>
              </div>
              {data.instructions && (
                <p className="mt-5 text-sm text-zinc-300 leading-relaxed border-l-2 border-[#E50914]/50 pl-4" data-testid="property-instructions">{data.instructions}</p>
              )}
            </div>

            {data.announcements?.length > 0 && (
              <div className="mt-6 space-y-3" data-testid="property-announcements">
                {data.announcements.map((a, i) => (
                  <div key={i} className="card-dark p-5 border-l-2 !border-l-[#E50914]">
                    <p className="font-semibold text-sm">{a.title}</p>
                    <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{a.message}</p>
                  </div>
                ))}
              </div>
            )}

            <p className="section-label mt-10">Spot types &amp; rates</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="property-categories">
              {data.categories.map((c) => (
                <div key={c.code} className={`card-dark p-6 ${data.selected_category === c.code ? "ring-1 ring-[#E50914]/60" : ""}`} data-testid={`landing-category-${c.code}`}>
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="font-display font-bold text-lg">{c.name}</h2>
                    {data.selected_category === c.code && <span className="text-[9px] font-bold uppercase tracking-widest text-[#FF3B45]">Your sign</span>}
                  </div>
                  {c.description && <p className="text-xs text-zinc-500 mt-1.5">{c.description}</p>}
                  <p className="text-[11px] text-zinc-600 mt-2">{c.spots_total} spots in this area</p>
                  <div className="mt-4 space-y-2">
                    {c.rate_plans.map((r) => (
                      <div key={r.rate_plan_id} className="flex items-center justify-between text-sm rounded-xl bg-white/[0.03] px-3.5 py-2.5" data-testid={`landing-rate-${r.rate_plan_id}`}>
                        <span className="text-zinc-300">{fmtDuration(r.duration_minutes)} {r.auto_renew_eligible && <span className="text-[10px] text-[#FF3B45] font-bold uppercase ml-1">auto-renew</span>}</span>
                        <span className="font-bold">{fmtCents(r.amount_cents, r.currency)} <span className="text-[10px] font-normal text-zinc-500">{RATE_LABEL[r.kind]}</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {user ? (
              <div className="mt-8"><PropertyBooking property={data} selectedCategory={data.selected_category} /></div>
            ) : (
              <div className="card-dark mt-8 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="property-book-cta">
                <ShieldCheck className="w-8 h-8 text-[#FF3B45] shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-sm">Sign in to book your spot here.</p>
                  <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                    Use your MyRedPark account — we'll bring you right back to this property.
                  </p>
                </div>
                <Link to="/login" state={{ from: `/p/${data.property_code}${data.selected_category ? `/${data.selected_category}` : ""}` }}
                      className="btn-primary text-sm shrink-0" data-testid="property-signin">
                  Sign in to continue
                </Link>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
